<!--
  add:
    promo code picker ?

  test:
    email_domain
    age
    total_spent
    visited_stores_in
-->
<template>
  <div>
    <div class="sideways-page-one">
      <div class="page-bar">
        <breadcrumb :crumbs="breadcrumbArray" />
      </div>
      <h1 class="page-title">
        Group Rules
      </h1>
      <div class="note note-info">
        <p>Define rules to decide which of your users are included in {{ group.name }}.</p>
      </div>

      <!-- CREATE START -->
      <div
        v-if="can()"
        class="portlet box blue-hoki margin-top-20"
      >
        <div
          class="portlet-title bg-blue-chambray"
          @click="toggleCreate()"
        >
          <div class="caption">
            <i class="fa fa-plus-circle" />
            Create New Rule
          </div>
          <div class="tools">
            <a :class="{'expand': !collapseCreate, 'collapse': collapseCreate}" />
          </div>
        </div>
        <div
          class="portlet-body"
          :class="{'display-hide': collapseCreate}"
        >
          <form
            role="form"
            @submit.prevent="submitCreate()"
          >
            <div class="row">
              <div class="col-md-12">
                <div
                  v-show="createErrorMessage"
                  ref="createErrorMessage"
                  class="alert alert-danger"
                >
                  <button
                    class="close"
                    data-close="alert"
                    @click.prevent="clearError('createErrorMessage')"
                  />
                  <span>{{ createErrorMessage }}</span>
                </div>
              </div>
            </div>
            <div class="display-flex flex-direction-column">
              <div class="display-flex flex-wrap-wrap align-items-center mb-1em">
                <p class="my-0 mr-1em">
                  Include users who
                </p>
                <el-select
                  v-model="creatingParameter"
                  placeholder="Select"
                  class="mr-1em min-width-250px"
                >
                  <el-option
                    value="sign-up-date"
                    label="signed up"
                  />
                  <el-option
                    value="city"
                    label="live in a city"
                  />
                  <el-option
                    value="province"
                    label="live in a province"
                  />
                  <el-option
                    value="email_domain"
                    label="use an email provider"
                  />
                  <el-option
                    value="promocode"
                    label="used a promo code"
                  />
                  <el-option
                    value="item_sku"
                    label="bought an item"
                  />
                  <el-option
                    value="age"
                    label="are in an age group"
                  />
                  <el-option
                    value="total-orders"
                    label="made a number of orders"
                  />
                  <el-option
                    value="total_spent"
                    label="spent a total"
                  />
                  <el-option
                    value="visited_stores_in"
                    label="visited a store in"
                  />
                </el-select>
              </div>
              <div class="display-flex flex-wrap-wrap align-items-center">
                <template v-if="creatingParameter === 'sign-up-date'">
                  <el-select
                    v-model="newOperator"
                    placeholder="before"
                    class="mr-1em"
                  >
                    <el-option
                      label="before"
                      value="<"
                    />
                    <el-option
                      label="on"
                      value="="
                    />
                    <el-option
                      label="after"
                      value=">"
                    />
                  </el-select>
                  <el-date-picker
                    v-model="newValue"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    :clearable="false"
                    placeholder="YYYY-MM-DD"
                  />
                </template>
                <template v-if="creatingParameter === 'city'">
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="Toronto"
                    >
                    <label :for="`create-${creatingParameter}`">
                      City
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'province'">
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="Ontario"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Province
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'email_domain'">
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="gmail.com"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Domain
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'total-orders'">
                  <el-select
                    v-model="newOperator"
                    placeholder="fewer than"
                    class="mr-1em"
                  >
                    <el-option
                      label="fewer than"
                      value="<"
                    />
                    <el-option
                      label="exactly"
                      value="="
                    />
                    <el-option
                      label="more than"
                      value=">"
                    />
                  </el-select>
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="5"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Number of orders
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'promocode'">
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="10OFF"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Promo code
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'item_sku'">
                  <menu-and-modifier-item-picker
                    v-if="showCreatePicker"
                    class="width-100"
                    @update="setCreateValue"
                  />
                  <template v-else>
                    <div class="form-group form-md-line-input">
                      <input
                        :id="`create-${creatingParameter}`"
                        v-model="newValue"
                        type="text"
                        class="form-control input-sm edited"
                        placeholder="BURGER1"
                      >
                      <label :for="`create-${creatingParameter}`">
                        SKU
                      </label>
                    </div>
                    <button
                      type="button"
                      class="btn btn-outline blue ml-1em"
                      :disabled="creating"
                      @click="openCreatePicker()"
                    >
                      Select
                    </button>
                  </template>
                </template>
                <template v-if="creatingParameter === 'age'">
                  <el-select
                    v-model="newOperator"
                    placeholder="under"
                    class="mr-1em"
                  >
                    <el-option
                      label="under"
                      value="<"
                    />
                    <el-option
                      label="exactly"
                      value="="
                    />
                    <el-option
                      label="over"
                      value=">"
                    />
                  </el-select>
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="25"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Age
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'total_spent'">
                  <el-select
                    v-model="newOperator"
                    placeholder="less than"
                    class="mr-1em"
                  >
                    <el-option
                      label="less than"
                      value="<"
                    />
                    <el-option
                      label="equal to"
                      value="="
                    />
                    <el-option
                      label="more than"
                      value=">"
                    />
                  </el-select>
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="10"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Amount
                    </label>
                  </div>
                </template>
                <template v-if="creatingParameter === 'visited_stores_in'">
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`create-${creatingParameter}`"
                      v-model="newValue"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="Alberta"
                    >
                    <label :for="`create-${creatingParameter}`">
                      Province
                    </label>
                  </div>
                </template>
              </div>
            </div>
            <div class="form-actions right">
              <button
                type="submit"
                class="btn blue"
                :disabled="creating"
              >
                {{ showCreatePicker ? 'Done' : 'Create' }}
                <i
                  v-show="creating"
                  class="fa fa-spinner fa-pulse fa-fw"
                />
              </button>
            </div>
          </form>
        </div>
      </div>
      <!-- CREATE END -->

      <!-- LIST START -->
      <div>
        <div class="portlet light portlet-fit bordered margin-top-20">
          <div class="portlet-title bg-blue-chambray">
            <div class="menu-image-main">
              <img src="@/../public/client_logo.png">
            </div>
            <div class="caption">
              <span class="caption-subject font-default bold uppercase">
                User Group Rules
              </span>
              <div class="caption-desc font-grey-cascade">
                This group includes users who meet at least one of these conditions.
              </div>
            </div>
          </div>
          <div class="portlet-body relative-block">
            <div class="mt-element-list">
              <div class="mt-list-container list-news">
                <div class="row">
                  <div class="col-md-12">
                    <div
                      v-show="listErrorMessage"
                      ref="listErrorMessage"
                      class="alert alert-danger"
                    >
                      <button
                        class="close"
                        @click="clearError('listErrorMessage')"
                      />
                      <span>{{ listErrorMessage }}</span>
                    </div>
                  </div>
                </div>
                <loading-screen
                  :show="loading"
                  :color="'#2C3E50'"
                  :display="'inline'"
                />
                <ul v-show="existing.length && !loading">
                  <li
                    v-for="rule in sorted"
                    :id="'rule-' + rule.id"
                    :key="rule.id"
                    class="mt-list-item actions-at-left margin-top-15"
                  >
                    <div class="list-item-actions">
                      <el-tooltip
                        v-if="can()"
                        content="Edit"
                        effect="light"
                        placement="right"
                      >
                        <a
                          class="btn btn-circle btn-icon-only btn-default"
                          @click.stop="openEditModal(rule)"
                        >
                          <i class="fa fa-lg fa-pencil" />
                        </a>
                      </el-tooltip>
                      <el-tooltip
                        v-if="can()"
                        content="Delete"
                        effect="light"
                        placement="right"
                      >
                        <a
                          class="btn btn-circle btn-icon-only btn-default"
                          @click.stop="openDeleteModal(rule)"
                        >
                          <i class="fa fa-lg fa-trash" />
                        </a>
                      </el-tooltip>
                    </div>
                    <div class="list-item-content height-mod">
                      <div class="col-md-8">
                        <span> {{ getOperatorLabel(rule) }} </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <no-results
              v-if="!existing.length && !loading"
              :show="true"
              :type="'rules'"
            />
          </div>
        </div>
      </div>
      <!-- LIST END -->

    <!-- EDIT END -->
    </div>
    <!-- EDIT START -->
    <modal
      ref="edit"
      :show="!!toEdit.id"
      effect="fade"
      :width="showUpdatePicker ? 900 : null"
      @closeOnEscape="closeEditModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeEditModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Edit
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="editErrorMessage"
          ref="editErrorMessage"
          class="row"
        >
          <div class="col-md-6">
            <div class="alert alert-danger">
              <button
                class="close"
                @click.prevent="clearError('editErrorMessage')"
              />
              <span>{{ editErrorMessage }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <p>Include users who {{ parameterLabels[toEdit.parameter] }}...</p>
            <div class="display-flex flex-wrap-wrap align-items-center">
              <template v-if="toEdit.parameter === 'sign-up-date'">
                <el-select
                  v-model="toEdit.operator"
                  placeholder="before"
                  class="mr-1em"
                >
                  <el-option
                    label="before"
                    value="<"
                  />
                  <el-option
                    label="on"
                    value="="
                  />
                  <el-option
                    label="after"
                    value=">"
                  />
                </el-select>
                <el-date-picker
                  v-model="toEdit.value"
                  type="date"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                  :clearable="false"
                  placeholder="YYYY-MM-DD"
                />
              </template>
              <template v-if="toEdit.parameter === 'city'">
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="Toronto"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    City
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'province'">
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="Ontario"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Province
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'email_domain'">
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="gmail.com"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Domain
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'total-orders'">
                <el-select
                  v-model="toEdit.operator"
                  placeholder="before"
                  class="mr-1em"
                >
                  <el-option
                    label="fewer than"
                    value="<"
                  />
                  <el-option
                    label="exactly"
                    value="="
                  />
                  <el-option
                    label="more than"
                    value=">"
                  />
                </el-select>
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="5"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Number of orders
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'promocode'">
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="5"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Promo code
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'item_sku'">
                <menu-and-modifier-item-picker
                  v-if="showUpdatePicker"
                  class="width-100"
                  @update="setUpdateValue"
                />
                <template v-else>
                  <div class="form-group form-md-line-input">
                    <input
                      :id="`edit-${toEdit.parameter}`"
                      v-model="toEdit.value"
                      type="text"
                      class="form-control input-sm edited"
                      placeholder="BURGER1"
                    >
                    <label :for="`edit-${toEdit.parameter}`">
                      SKU
                    </label>
                  </div>
                  <button
                    type="button"
                    class="btn btn-outline blue ml-1em"
                    :disabled="updating"
                    @click="openUpdatePicker()"
                  >
                    Select
                  </button>
                </template>
              </template>
              <template v-if="toEdit.parameter === 'age'">
                <el-select
                  v-model="toEdit.operator"
                  placeholder="before"
                  class="mr-1em"
                >
                  <el-option
                    label="under"
                    value="<"
                  />
                  <el-option
                    label="exactly"
                    value="="
                  />
                  <el-option
                    label="over"
                    value=">"
                  />
                </el-select>
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="25"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Age
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'total_spent'">
                <el-select
                  v-model="toEdit.operator"
                  placeholder="before"
                  class="mr-1em"
                >
                  <el-option
                    label="less than"
                    value="<"
                  />
                  <el-option
                    label="exactly"
                    value="="
                  />
                  <el-option
                    label="more than"
                    value=">"
                  />
                </el-select>
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="10"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Amount
                  </label>
                </div>
              </template>
              <template v-if="toEdit.parameter === 'visited_stores_in'">
                <div class="form-group form-md-line-input">
                  <input
                    :id="`edit-${toEdit.parameter}`"
                    v-model="toEdit.value"
                    type="text"
                    class="form-control input-sm edited"
                    placeholder="10"
                  >
                  <label :for="`edit-${toEdit.parameter}`">
                    Province
                  </label>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer clear"
      >
        <div class="row">
          <div class="col-md-12">
            <button
              type="button"
              class="btn blue pull-right"
              :disabled="updating"
              @click="submitUpdate()"
            >
              {{ showUpdatePicker ? 'Done' : 'Save' }}
              <i
                v-show="updating"
                class="fa fa-spinner fa-pulse fa-fw"
              />
            </button>
          </div>
        </div>
      </div>
    </modal>

    <!-- DELETE START -->
    <modal
      ref="edit"
      :show="!!toDelete.id"
      effect="fade"
      @closeOnEscape="closeDeleteModal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        <button
          type="button"
          class="close"
          @click="closeDeleteModal()"
        >
          <span>&times;</span>
        </button>
        <h4 class="modal-title center">
          Delete
        </h4>
      </div>
      <div
        slot="modal-body"
        class="modal-body"
      >
        <div
          v-show="deleteErrorMessage"
          ref="deleteErrorMessage"
          class="row"
        >
          <div class="col-md-6">
            <div class="alert alert-danger">
              <button
                class="close"
                @click.prevent="clearError('deleteErrorMessage')"
              />
              <span>{{ deleteErrorMessage }}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <p>Do you want to delete this rule?</p>
            <p class="ml-1em bold">
              Include users who {{ getOperatorLabel(toDelete) }}
            </p>
          </div>
        </div>
      </div>
      <div
        slot="modal-footer"
        class="modal-footer clear"
      >
        <div class="row">
          <div class="col-md-12">
            <button
              type="button"
              class="btn blue pull-right"
              :disabled="deleting"
              @click="deleteRule()"
            >
              Delete
              <i
                v-show="deleting"
                class="fa fa-spinner fa-pulse fa-fw"
              />
            </button>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from '@/components/modules/Modal'
import Breadcrumb from '@/components/modules/Breadcrumb'
import LoadingScreen from '@/components/modules/LoadingScreen'
import MenuAndModifierItemPicker from '@/components/modules/MenuAndModifierItemPicker'
import NoResults from '@/components/modules/NoResults'
import UserGroupsFunctions from '@/controllers/UserGroups'
import ajaxErrorHandler from '@/controllers/ErrorController'
import { isNonNegativeNumber } from '@/controllers/utils'
import { mapGetters } from 'vuex'

export default {
  name: 'UserGroupRules',
  components: {
    Breadcrumb,
    LoadingScreen,
    MenuAndModifierItemPicker,
    Modal,
    NoResults
  },
  data: function () {
    return {
      collapseCreate: true,

      loading: false,
      creating: false,
      updating: false,
      deleting: false,

      showCreatePicker: false,
      showUpdatePicker: false,

      group: {}, // set in created() from query

      existing: [
        {
          id: 14,
          operator: '<',
          parameter: 'sign-up-date',
          user_group_id: 11,
          value: '2019-01-01'
        }
      ],

      createErrorMessage: '',
      listErrorMessage: '',
      editErrorMessage: '',
      deleteErrorMessage: '',

      parameterLabels: {
        'sign-up-date': 'signed up',
        'city': 'live in the city of',
        'province': 'live in the province of',
        'email_domain': 'use the email provider',
        'total-orders': 'placed a number of orders',
        'promocode': 'used the promo code',
        'item_sku': 'purchased the item',
        'age': 'are aged',
        'total_spent': 'spent a total of',
        'visited_stores_in': 'visited a store in the province of'
      },

      breadcrumbArray: [
        { name: 'Users Manager', link: false },
        { name: 'User Groups', link: false }
      ],

      createDrafts: this.getBlankTemplates(),
      creatingParameter: 'sign-up-date',
      toCreate: {
        value: '',
        parameter: ''
      },

      toEdit: {},

      toDelete: {}
    }
  },
  computed: {
    newValue: {
      set (value) {
        this.createDrafts[this.creatingParameter].value = value
      },
      get () {
        return this.createDrafts[this.creatingParameter].value
      }
    },
    newOperator: {
      set (value) {
        this.createDrafts[this.creatingParameter].operator = value
      },
      get () {
        return this.createDrafts[this.creatingParameter].operator
      }
    },
    sorted () {
      return this.existing
    },
    ...mapGetters(['can'])
  },
  watch: {
    creatingParameter () {
      this.showCreatePicker = false
    }
  },
  created () {
    const { group } = this.$route.query
    if (!group) {
      this.$router.push({
        name: 'User Groups'
      })
      return
    }

    this.group = JSON.parse(group)
    this.$router.replace({ query: {} })
    this.getRules()
  },
  methods: {
    openCreatePicker () {
      this.showCreatePicker = true
    },
    openUpdatePicker () {
      this.showUpdatePicker = true
    },
    setCreateValue (value) {
      if (value.length) {
        this.newValue = value[0]
      }
      this.showCreatePicker = false
    },
    setUpdateValue (value) {
      if (value.length) {
        this.toEdit.value = value[0]
      }
      this.showUpdatePicker = false
    },
    clearError (name) {
      this[name] = ''
    },
    getOperatorLabel ({ parameter, operator, value }) {
      if (parameter === 'sign-up-date') {
        const labels = {
          '=': 'on',
          '<': 'before',
          '>': 'after'
        }
        return `signed up ${labels[operator]} ${value}`
      }
      if (parameter === 'city') return `live in the city of ${value}`
      if (parameter === 'province') return `live in the province of ${value}`
      if (parameter === 'email_domain') return `use ${value} as their email provider`
      if (parameter === 'promocode') return `used the promo code ${value}`
      if (parameter === 'item_sku') return `purchased items with the SKU ${value}`
      if (parameter === 'visited_stores_in') return `visited stores in the province of ${value}`
      if (parameter === 'age') {
        const labels = {
          '=': '',
          '>': 'more than',
          '<': 'less than'
        }
        return `are ${labels[operator]} ${value} year${value === '1' ? '' : 's'} old`
      }
      if (parameter === 'total-orders') {
        const labels = {
          '=': '',
          '>': 'more than',
          '<': 'fewer than'
        }
        return `made ${labels[operator]} ${value} order${value === '1' ? '' : 's'}`
      }
      if (parameter === 'total_spent') {
        const labels = {
          '=': '',
          '>': 'more than',
          '<': 'less than'
        }
        return `spent ${labels[operator]} ${value}`
      }
      return ''
    },
    openEditModal (rule) {
      this.toEdit = { ...rule }
    },
    closeEditModal () {
      this.toEdit = {}
    },
    openDeleteModal (rule) {
      this.toDelete = { ...rule }
    },
    closeDeleteModal () {
      this.toDelete = {}
    },
    getBlankTemplates () {
      return {
        'sign-up-date': {
          parameter: 'sign-up-date',
          value: '',
          operator: ''
        },
        'city': {
          parameter: 'city',
          value: '',
          operator: '='
        },
        'province': {
          parameter: 'province',
          value: '',
          operator: '='
        },
        'email_domain': {
          parameter: 'email_domain',
          value: '',
          operator: '='
        },
        'total-orders': {
          parameter: 'total-orders',
          value: '',
          operator: ''
        },
        'promocode': {
          parameter: 'promocode',
          value: '',
          operator: '='
        },
        'item_sku': {
          parameter: 'item_sku',
          value: '',
          operator: '='
        },
        'age': {
          parameter: 'age',
          value: '',
          operator: ''
        },
        'total_spent': {
          parameter: 'total_spent',
          value: '',
          operator: ''
        },
        'visited_stores_in': {
          parameter: 'visited_stores_in',
          value: '',
          operator: '='
        }
      }
    },
    toggleCreate () {
      this.collapseCreate = !this.collapseCreate
    },
    /**
     * To get a list of store groups.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getRules () {
      this.clearError('listErrorMessage')
      this.loading = true
      var _this = this
      return UserGroupsFunctions.getRules(this.group.id)
        .then(response => {
          _this.existing = response.payload
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not fetch rules for this group',
            errorName: 'listErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          _this.loading = false
        })
    },
    submitUpdate () {
      if (this.showUpdatePicker) {
        this.showUpdatePicker = false
      } else {
        this.validateUpdated()
      }
    },
    submitCreate () {
      if (this.showCreatePicker) {
        this.showCreatePicker = false
      } else {
        this.validateNew()
      }
    },
    validateUpdated () {
      if (!this.toEdit.value || !this.toEdit.operator) {
        this.editErrorMessage = 'All fields are required'
      } else if (
        (
          this.toEdit.parameter === 'total_spent' ||
          this.toEdit.parameter === 'total-orders' ||
          this.toEdit.parameter === 'age'
        ) &&
        !isNonNegativeNumber(this.toEdit.value)
      ) {
        this.editErrorMessage = 'Please enter a number'
      } else {
        this.updateRule()
      }
    },
    validateNew () {
      if (!this.newValue) {
        this.createErrorMessage = 'All fields are required'
      } else if (
        (
          this.creatingParameter === 'total_spent' ||
          this.creatingParameter === 'total-orders' ||
          this.creatingParameter === 'age'
        ) &&
        !isNonNegativeNumber(this.newValue)
      ) {
        this.createErrorMessage = 'Please enter a number'
      } else {
        this.createRule()
      }
    },
    /**
     * To get a list of store groups.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    createRule () {
      this.clearError('createErrorMessage')
      this.creating = true
      var _this = this
      const payload = {
        'user_group_id': this.group.id,
        'parameter': this.creatingParameter,
        'value': this.newValue,
        'operator': this.newOperator
      }
      return UserGroupsFunctions.createRule(payload)
        .then(response => {
          const reset = this.getBlankTemplates()[this.creatingParameter]
          this.createDrafts = {
            ...this.createDrafts,
            [this.creatingParameter]: reset
          }
          this.getRules()
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not create the rule',
            errorName: 'createErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          _this.creating = false
        })
    },
    updateRule () {
      this.clearError('editErrorMessage')
      this.updating = true
      var _this = this
      const payload = {
        'id': this.toEdit.id,
        'user_group_id': this.toEdit.user_group_id,
        'parameter': this.toEdit.parameter,
        'value': this.toEdit.value,
        'operator': this.toEdit.operator
      }
      return UserGroupsFunctions.updateRule(payload)
        .then(response => {
          this.getRules()
          this.closeEditModal()
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not save the rule',
            errorName: 'editErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          _this.updating = false
        })
    },
    deleteRule () {
      this.clearError('deleteErrorMessage')
      this.deleting = true
      var _this = this
      return UserGroupsFunctions.deleteRule(this.toDelete)
        .then(response => {
          this.getRules()
          this.closeDeleteModal()
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not delete the rule',
            errorName: 'deleteErrorMessage',
            vue: _this
          })
        })
        .finally(() => {
          _this.deleting = false
        })
    }
  }
}
</script>

<style scoped>
.mt-element-list .list-news.mt-list-container ul>.mt-list-item:hover,
.mt-element-list .list-news.mt-list-container ul>.mt-list-item:last-child {
  background-color: white;
}
</style>
